import { useEffect, useMemo, useState } from 'react';

import { RecentFilterMode, RecentJob } from '../../components/molecules/RecentJobs';
import { ColumnSelectionItem } from '../../components/templates/ColumnSelection';
import { columnSelectionItems } from '../../constants/service.constant';
import { useGetServicePaginatedData } from '../../queries/service-query';
import { getRecentlyAccessedJobs, formatRecentJobTime } from '../../utils/recent-jobs';

export interface UseRecentJobsReturn {
  recentJobs: RecentJob[];
  recentJobsAnchor: HTMLElement | null;
  recentFilter: RecentFilterMode;
  setRecentJobsAnchor: (el: HTMLElement | null) => void;
  setRecentFilter: (mode: RecentFilterMode) => void;
}

export const useRecentJobs = (): UseRecentJobsReturn => {
  const [recentJobsAnchor, setRecentJobsAnchor] = useState<HTMLElement | null>(null);
  const [recentFilter, setRecentFilter] = useState<RecentFilterMode>('accessed');

  const recentSortField = recentFilter === 'created' ? 'createdAt' : 'updatedAt';
  const recentQuery = useGetServicePaginatedData(
    { filterModel: { items: [] }, page: 0, pageSize: 25, sortModel: [{ field: recentSortField, sort: 'desc' }] },
    columnSelectionItems as unknown as ColumnSelectionItem[],
  );

  useEffect(() => {
    if (recentFilter !== 'accessed' && Boolean(recentJobsAnchor)) {
      recentQuery.refetch();
    }
  }, [recentFilter, recentJobsAnchor]);

  const recentJobs: RecentJob[] = useMemo(() => {
    if (recentFilter === 'accessed') {
      return getRecentlyAccessedJobs().map((j) => ({
        id: j.id,
        jobId: j.jobId,
        client: j.client,
        location: j.location,
        time: formatRecentJobTime(j.accessedAt),
      }));
    }
    const apiData = recentQuery.data?.data ?? [];
    return apiData.map((row) => ({
      id: row.id,
      jobId: `JOB-${row.id}`,
      client: row.clientHierarchy ?? '',
      location: row.locationName ?? '',
      time: recentFilter === 'created'
        ? new Date(row.createdAt ?? '').toLocaleDateString()
        : new Date(row.updatedAt ?? '').toLocaleDateString(),
    }));
  }, [recentFilter, recentQuery.data, recentJobsAnchor]);

  return {
    recentJobs,
    recentJobsAnchor,
    recentFilter,
    setRecentJobsAnchor,
    setRecentFilter,
  };
};
